import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AcceptInvitePage() {
  const { inviteToken } = useParams();
  const navigate = useNavigate();
  // phases: loading | form | done | error
  const [phase, setPhase] = useState('loading');
  const [info, setInfo] = useState(null);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API}/team/invite-info/${inviteToken}`);
        const data = await res.json();
        if (!res.ok) { setPhase('error'); setMessage(data.detail || 'This invitation is invalid or has expired.'); return; }
        setInfo(data);
        setName(data.name || '');
        if (data.is_new_user) {
          setPhase('form');
        } else {
          // Existing Nivela account — just attach to the team, then send to login.
          const r2 = await fetch(`${API}/team/accept-invite/${inviteToken}`, {
            method: 'POST', credentials: 'include',
            headers: { 'Content-Type': 'application/json' }, body: '{}',
          });
          const d2 = await r2.json();
          if (r2.ok) { setPhase('done'); setMessage(d2.message || "You've joined — please log in."); }
          else { setPhase('error'); setMessage(d2.detail || 'Could not accept the invitation.'); }
        }
      } catch (e) { setPhase('error'); setMessage('Network error. Please try again.'); }
    };
    if (inviteToken) load();
  }, [inviteToken]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { setMessage('Password must be at least 6 characters.'); return; }
    setSubmitting(true); setMessage('');
    try {
      const res = await fetch(`${API}/team/accept-invite/${inviteToken}`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, password }),
      });
      const data = await res.json();
      if (res.ok) {
        if (data.logged_in) { navigate('/dashboard', { replace: true }); return; }
        setPhase('done'); setMessage(data.message || 'Account ready — please log in.');
      } else { setMessage(data.detail || 'Could not accept the invitation.'); }
    } catch (e) { setMessage('Network error. Please try again.'); }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-[#F7FAFB] flex items-center justify-center p-6" data-testid="accept-invite-page">
      <Card className="w-full max-w-md border-[#E2E8ED] shadow-sm">
        <CardContent className="py-10 px-8">
          {phase === 'loading' && (
            <div className="text-center">
              <Loader2 className="w-10 h-10 text-[#1D4053] animate-spin mx-auto mb-4" />
              <p className="text-sm text-[#446E87]">Loading your invitation…</p>
            </div>
          )}

          {phase === 'error' && (
            <div className="text-center">
              <XCircle className="w-10 h-10 text-red-500 mx-auto mb-4" />
              <h2 className="text-lg font-bold text-[#0D1830] mb-2">Invitation Error</h2>
              <p className="text-sm text-[#446E87] mb-6">{message}</p>
              <Button onClick={() => navigate('/auth')} className="bg-[#0D1830] text-white rounded-full hover:opacity-90">Go to Login</Button>
            </div>
          )}

          {phase === 'done' && (
            <div className="text-center">
              <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-4" />
              <h2 className="text-lg font-bold text-[#0D1830] mb-2">You're in!</h2>
              <p className="text-sm text-[#446E87] mb-6">{message}</p>
              <Button onClick={() => navigate('/auth')} className="bg-[#0D1830] text-white rounded-full hover:opacity-90">Go to Login</Button>
            </div>
          )}

          {phase === 'form' && info && (
            <form onSubmit={submit} data-testid="accept-invite-form">
              <h2 className="text-xl font-bold text-[#0D1830] mb-1">Join {info.company_name}</h2>
              <p className="text-sm text-[#446E87] mb-6">
                You've been invited as a <strong>{info.role}</strong>. Set a password to finish creating your account for <strong>{info.email}</strong>.
              </p>
              <label className="block text-xs font-medium text-[#446E87] mb-1">Your name</label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className="mb-4" />
              <label className="block text-xs font-medium text-[#446E87] mb-1">Password</label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 6 characters" className="mb-2" required data-testid="invite-password" />
              {message && <p className="text-xs text-red-500 mb-2">{message}</p>}
              <Button type="submit" disabled={submitting} className="w-full mt-4 bg-[#0D1830] text-white rounded-full hover:opacity-90 disabled:opacity-50" data-testid="invite-submit">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Create account & join'}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
